import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldAlert, HeartPulse, Shield, Users, Car, FileWarning, BookOpen, Phone, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, StatusPill } from "@/components/shared";

const OPTIONS = [
  { type: "Harassment / Unsafe Situation", icon: ShieldAlert, color: "var(--cs-amber)" },
  { type: "Medical Assistance", icon: HeartPulse, color: "var(--cs-blue)" },
  { type: "Security Support", icon: Shield, color: "var(--cs-blue)" },
  { type: "I'm Separated From My Crew", icon: Users, color: "var(--cs-magenta)" },
  { type: "Need a Safe Ride", icon: Car, color: "var(--cs-green)" },
  { type: "Report an Incident", icon: FileWarning, color: "var(--cs-amber)" },
  { type: "Access Support Resources", icon: BookOpen, color: "var(--cs-fg2)" },
];

export default function Help() {
  const nav = useNavigate();
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [mine, setMine] = useState([]);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/me/help").then(({ data }) => setMine(data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/help", { type: selected.type, note });
      toast.success("Help requested. Band safety staff have been notified.");
      setSelected(null); setNote("");
      load();
    } catch { toast.error("Could not send. If in danger, call 911."); }
    finally { setSending(false); }
  };

  return (
    <Page className="px-5 pt-6">
      <button data-testid="help-back" onClick={() => nav(-1)} className="cs-tap flex items-center gap-1 text-sm mb-4" style={{ color: "var(--cs-fg2)" }}>
        <ArrowLeft size={18} /> Back
      </button>
      <h1 className="font-head font-extrabold text-3xl">I Need Help</h1>
      <p className="mt-1 text-base" style={{ color: "var(--cs-fg2)" }}>Get the right support, faster.</p>

      {!selected ? (
        <>
          <div className="mt-5 space-y-2">
            {OPTIONS.map((o) => (
              <button key={o.type} data-testid={`help-option-${o.type.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setSelected(o)}
                className="cs-tap w-full cs-card p-4 flex items-center gap-3 text-left">
                <span className="grid place-items-center rounded-xl flex-shrink-0" style={{ width: 44, height: 44, background: `${o.color}22` }}>
                  <o.icon size={22} style={{ color: o.color }} />
                </span>
                <span className="font-semibold">{o.type}</span>
              </button>
            ))}
          </div>

          <a data-testid="help-call-911" href="tel:911"
            className="cs-tap flex items-center justify-center gap-2 mt-5 rounded-2xl font-head font-bold text-lg no-underline"
            style={{ height: 58, background: "var(--cs-red)", color: "#fff" }}>
            <Phone size={20} /> CALL 911
          </a>
          <p className="text-center text-xs mt-2" style={{ color: "var(--cs-fg2)" }}>
            Immediate danger only. Carnival Safety 101 does not replace emergency services.
          </p>

          {mine.length > 0 && (
            <div className="mt-8">
              <p className="text-xs font-bold tracking-wide mb-2" style={{ color: "var(--cs-fg2)" }}>YOUR REQUESTS</p>
              <div className="space-y-2">
                {mine.map((r) => (
                  <div key={r.id} data-testid={`help-my-request-${r.id}`} className="cs-card p-3 flex items-center justify-between">
                    <p className="text-sm font-semibold pr-2">{r.type}</p>
                    <StatusPill status={r.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mt-5">
          <div className="cs-card p-4">
            <p className="text-xs font-bold tracking-wide" style={{ color: "var(--cs-fg2)" }}>REQUESTING</p>
            <p className="font-head font-bold text-xl mt-1">{selected.type}</p>
            <textarea data-testid="help-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="Add a short note (optional) — what's happening, where you are…"
              className="w-full mt-3 rounded-xl p-3 outline-none" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)", color: "#fff", resize: "none", fontSize: 16 }} />
          </div>
          <button data-testid="help-submit-button" onClick={submit} disabled={sending}
            className="cs-tap w-full mt-4 magenta-grad magenta-glow rounded-2xl text-white font-head font-bold text-lg flex items-center justify-center gap-2" style={{ height: 58 }}>
            <Check size={20} /> {sending ? "Sending…" : "SEND REQUEST"}
          </button>
          <button data-testid="help-cancel-button" onClick={() => setSelected(null)}
            className="cs-tap w-full mt-2 rounded-2xl font-semibold" style={{ height: 50, background: "var(--cs-card)", border: "1px solid var(--cs-border)", color: "#fff" }}>
            Back to options
          </button>
        </div>
      )}
    </Page>
  );
}
